import { CreditCardNetwork } from '@wix/cashier-common/dist/src/enums/payments/CreditCardNetwork';
import { getMaxNumber } from '../../../utils/getMaxNumber';
import { sanitizeCardNumber } from './sanitizeUtils';

export interface CardNetworkMeta {
  network: CreditCardNetwork;
  pattern?: RegExp;
  format: RegExp;
  lengths: number[];
  cvcLengths: number[];
  luhn: boolean;
  isValid?(cardNumber: string): boolean;
}

export const defaultNumberFormat = /(\d{1,4})/g;

export const cardNetworksMeta: CardNetworkMeta[] = [
  {
    network: CreditCardNetwork.VisaElectron,
    pattern: /^4(026|17500|405|508|844|91[37])/,
    format: defaultNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    network: CreditCardNetwork.Isracard,
    // based on https://gist.github.com/avimar/e7421f96357bf06acc31d952c7baf84e
    isValid(cardNumber: string) {
      // NOTE: based on https://wix.atlassian.net/browse/PAY-33365
      if (cardNumber.length === 10) {
        const validFirstDigit = ['1', '2', '3', '4', '5', '6', '7'];
        const validNextDigits = [
          '11',
          '14',
          '15',
          '18',
          '19',
          '21',
          '22',
          '39',
          '44',
          '53',
          '55',
          '56',
          '73',
          '81',
          '82',
          '83',
          '85',
          '86',
          '95',
          '96',
        ];

        const firstDigit = cardNumber.charAt(0);
        const nextTwoDigits = cardNumber.substring(1, 3);

        return validFirstDigit.includes(firstDigit) && validNextDigits.includes(nextTwoDigits);
      }
      if (cardNumber.length < 8 || cardNumber.length > 9) {
        return false;
      }
      const checkSum: number = cardNumber
        .split('')
        .map(Number)
        .reduce((acc, curr, i) => acc + curr * (cardNumber.length - i), 0);

      return checkSum % 11 === 0;
    },
    format: defaultNumberFormat,
    lengths: [8, 9, 10],
    cvcLengths: [3],
    luhn: false,
  },
  {
    network: CreditCardNetwork.Forbrugsforeningen,
    pattern: /^600/,
    format: defaultNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    network: CreditCardNetwork.Dankort,
    pattern: /^5019/,
    format: defaultNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    network: CreditCardNetwork.Visa,
    pattern: /^4/,
    format: defaultNumberFormat,
    lengths: [13, 16, 19],
    cvcLengths: [3],
    luhn: true,
  },
  {
    network: CreditCardNetwork.Amex,
    pattern: /^3[47]/,
    format: /(\d{1,4})(\d{1,6})?(\d{1,5})?/,
    lengths: [15],
    cvcLengths: [4],
    luhn: true,
  },
  {
    network: CreditCardNetwork.DinersClub,
    pattern: /^3[0689]/,
    format: defaultNumberFormat,
    lengths: [14, 16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    network: CreditCardNetwork.Discover,
    pattern: /^(6011|64[4-9]|65[^23]|6520|6521[0-4]|653[^01]|6531[5-9]|601300|3[123689]|309)/,
    format: defaultNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    network: CreditCardNetwork.UnionPay,
    pattern: /^(62|88)/,
    format: defaultNumberFormat,
    lengths: [16, 17, 18, 19],
    cvcLengths: [3],
    luhn: false,
  },
  {
    network: CreditCardNetwork.JCB,
    pattern: /^35/,
    format: defaultNumberFormat,
    lengths: [16, 17, 18, 19],
    cvcLengths: [3],
    luhn: true,
  },
  {
    network: CreditCardNetwork.RuPay,
    pattern: /^(508[5-9]|6069[8-9]|607[0-8]|6079[0-8]|608[0-5]|6521[5-9]|652[2-9]|6530|6531[0-4])/,
    format: defaultNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: false,
  },
  {
    network: CreditCardNetwork.Troy,
    pattern: /^9792/,
    format: defaultNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: false,
  },
  {
    network: CreditCardNetwork.Maestro,
    pattern:
      /^(50[^18]|501[^9]|5019[^357]|50193[^4]|508[1-4]|5[6789]|6[^0245]|62[79]|628[01]|626257|62292[78]|62293[^4]|62294[^14]|62299|62298[02369]|62297[^0]|62296[^4569]|62295[^68]|62212[^6789]|62211|6220|621[^4]|6214[^8]|62148[^3]|620|64[0-3]|60[2-5]|606[^9]|6069[^89]|60799|601[^13]|6013[^0]|60130[^0]|600)/,
    format: defaultNumberFormat,
    lengths: [12, 13, 14, 15, 16, 17, 18, 19],
    cvcLengths: [3],
    luhn: true,
  },
  {
    network: CreditCardNetwork.MasterCard,
    pattern: /^(5[1-5]|2[2-7])/,
    format: defaultNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
];

export const maxCardNumberLength: number = getMaxNumber(
  cardNetworksMeta.reduce<number[]>((lengths, meta) => {
    lengths.push(...meta.lengths);
    return lengths;
  }, []),
);

export const getCardNetworkMetaByNumber = (cardNumber: string): CardNetworkMeta => {
  const sanitizedCardNumber: string = sanitizeCardNumber(cardNumber);

  return cardNetworksMeta.find(
    (cardNetworkMeta) =>
      cardNetworkMeta.pattern?.test(sanitizedCardNumber) ||
      (cardNetworkMeta.isValid && cardNetworkMeta.isValid(sanitizedCardNumber)),
  );
};

export const getCardNetworkMetaByNetwork = (network: CreditCardNetwork): CardNetworkMeta =>
  cardNetworksMeta.find((card) => card.network === network);

export const getCardNetworkByNumber = (cardNumber: string): CreditCardNetwork => {
  if (!cardNumber) {
    return;
  }
  const cardNetworkMeta: CardNetworkMeta = getCardNetworkMetaByNumber(cardNumber);

  return cardNetworkMeta ? cardNetworkMeta.network : undefined;
};

export const getMaxCardLengthByNumber = (cardNumber: string): number => {
  const cardNetworkMeta: CardNetworkMeta = getCardNetworkMetaByNumber(cardNumber);

  return cardNetworkMeta ? getMaxNumber(cardNetworkMeta.lengths) : maxCardNumberLength;
};

/**
 * Returns max card number length for provided number. Cover case when matched 2+ card networks
 * @param cardNumber string
 * @returns number
 */
export const getMaxCardLengthByNumberToFocusNext = (cardNumber: string): number => {
  const sanitizedCardNumber: string = sanitizeCardNumber(cardNumber);
  const matchedCardNetworks = cardNetworksMeta.filter(
    (cardNetworkMeta) =>
      cardNetworkMeta.pattern?.test(sanitizedCardNumber) ||
      (cardNetworkMeta.isValid && cardNetworkMeta.isValid(sanitizedCardNumber)),
  );

  return (
    getMaxNumber(matchedCardNetworks.map((cardNetworkMeta) => getMaxNumber(cardNetworkMeta.lengths))) ||
    maxCardNumberLength
  );
};
